import { useTranslation } from "react-i18next";
import { formatCurrency, formatNumber } from "@/utils/formatters";
import { WrappedCardFrame } from "./WrappedCardFrame";
import type { BudgetsSavingsData, WrappedCard } from "../types";

interface BudgetsSavingsCardProps {
  card: WrappedCard;
}

export const BudgetsSavingsCard = ({ card }: BudgetsSavingsCardProps) => {
  const { t } = useTranslation("wrapped");
  const data = card.data as unknown as BudgetsSavingsData;

  const exceededCount = data.exceeded_count ?? Math.max(data.total - data.respected, 0);

  return (
    <WrappedCardFrame title={card.title} body={card.body}>
      {data.total_excess != null && data.balance_if_budgets_met != null ? (
        <p className="mb-4 rounded-[var(--ds-radius-md)] bg-[var(--app-primary-chip-bg)] px-4 py-3 text-sm font-semibold text-[var(--app-text)]">
          {t(
            data.balance_if_budgets_met < 0
              ? "wrapped.budgetCounterfactualNegative"
              : "wrapped.budgetCounterfactualPositive",
            {
              excess: formatCurrency(data.total_excess),
              balance: formatCurrency(data.balance_if_budgets_met),
            },
          )}
        </p>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="app-panel-raised-inset p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--app-muted-text)]">{t("wrapped.budgetsRespected")}</p>
          <p className="mt-2 font-[var(--app-font-display)] text-2xl font-semibold tabular-nums text-[var(--app-primary)]">
            {formatNumber(data.respected)} / {formatNumber(data.total)}
          </p>
        </div>
        <div className="app-panel-raised-inset p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--app-muted-text)]">{t("wrapped.deposited")}</p>
          <p className="mt-2 font-[var(--app-font-display)] text-2xl font-semibold tabular-nums text-[var(--app-success-text)]">{formatCurrency(data.deposited)}</p>
        </div>
      </div>

      {data.exceeded.length > 0 ? (
        <div className="mt-4">
          <h3 className="text-sm font-semibold text-[var(--app-text)]">
            {t(exceededCount > data.exceeded.length ? "wrapped.topBudgetsExceeded" : "wrapped.budgetsExceeded")}
          </h3>
          <p className="mt-1 text-xs text-[var(--app-muted-text)]">
            {t("wrapped.budgetsExceededCount", { count: exceededCount, total: data.total })}
          </p>
          <ul className="mt-2 divide-y divide-[color:color-mix(in_srgb,var(--app-border)_72%,transparent)]">
            {data.exceeded.map((budget) => (
              <li key={budget.name} className="flex min-w-0 flex-wrap items-center justify-between gap-3 py-3 text-sm">
                <span className="min-w-0 break-words text-[var(--app-text-secondary)]">{budget.name}</span>
                <span className="shrink-0 tabular-nums text-[var(--app-danger-text)]">
                  {t("wrapped.budgetOverage", {
                    excess: formatCurrency(budget.excess ?? budget.spent - budget.limit),
                    limit: formatCurrency(budget.limit),
                  })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

    </WrappedCardFrame>
  );
};
